import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { usePlayer } from '../context/PlayerContext';
import { useAuth } from '../context/AuthContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Play, 
  Pause, 
  Heart, 
  Share2, 
  Download, 
  BookOpen,
  Music,
  Clock,
  CheckCircle,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SongDetail = () => {
  const { songId } = useParams();
  const { currentSong, isPlaying, playSong, pauseSong } = usePlayer();
  const { isAuthenticated, token } = useAuth();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lyricsData, setLyricsData] = useState(null);

  useEffect(() => {
    fetchSong();
  }, [songId]);

  const fetchSong = async () => {
    try {
      const [songRes, lyricsRes] = await Promise.all([
        axios.get(`${API}/songs/${songId}`),
        axios.get(`${API}/songs/${songId}/lyrics-scripture`).catch(() => null)
      ]);
      setSong(songRes.data);
      if (lyricsRes) {
        setLyricsData(lyricsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch song:', error);
      toast.error('Song not found');
    } finally {
      setLoading(false);
    }
  };

  const isCurrentSong = currentSong?.id === song?.id;

  const handlePlay = () => {
    if (isCurrentSong && isPlaying) {
      pauseSong();
    } else {
      playSong(song);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const renderLyricsWithScripture = (lyrics) => {
    if (!lyrics) return null;
    
    // Parse [Ref: Verse] tags
    const parts = lyrics.split(/(\[Ref:\s*[^\]]+\])/g);
    
    return parts.map((part, index) => {
      const match = part.match(/\[Ref:\s*([^\]]+)\]/);
      if (match) {
        const reference = match[1];
        return (
          <TooltipProvider key={index}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-m4-yellow cursor-pointer hover:underline font-scripture">
                  [{reference}]
                </span>
              </TooltipTrigger>
              <TooltipContent 
                side="top" 
                className="bg-black border border-m4-yellow p-4 max-w-xs"
              >
                <p className="font-scripture text-white">{reference}</p>
                <p className="text-xs text-neutral-400 mt-2">Click to view full verse</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-m4-yellow" />
        </div>
      </Layout>
    );
  }

  if (!song) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Music className="h-16 w-16 mx-auto mb-4 text-neutral-700" />
            <h2 className="font-heading text-2xl font-bold text-white">Song Not Found</h2>
            <Link to="/discover" className="text-m4-yellow hover:underline mt-2 inline-block">
              Browse Music
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const defaultCover = 'https://images.unsplash.com/photo-1581650127213-e72e2271ff15?w=600&h=600&fit=crop';

  return (
    <Layout>
      <div className="py-8 px-4 md:px-8">
        <div className="container mx-auto max-w-6xl">
          {/* Hero Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            {/* Cover Art */}
            <div className="lg:col-span-4">
              <div className="aspect-square bg-neutral-800 relative group">
                <img 
                  src={song.cover_image || defaultCover}
                  alt={song.title}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={handlePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="play-song-btn"
                >
                  <div className="h-20 w-20 rounded-full bg-m4-yellow flex items-center justify-center">
                    {isCurrentSong && isPlaying ? (
                      <Pause className="h-10 w-10 text-black" fill="black" />
                    ) : (
                      <Play className="h-10 w-10 text-black ml-1" fill="black" />
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Song Info */}
            <div className="lg:col-span-8 flex flex-col justify-center">
              <p className="text-sm text-m4-yellow uppercase tracking-widest mb-2">{song.genre}</p>
              <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
                {song.title}
              </h1>
              
              <Link 
                to={`/${song.artist_name?.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-xl text-neutral-300 hover:text-white transition-colors flex items-center gap-2 mb-6"
                data-testid="artist-link"
              >
                {song.artist_name}
                {song.is_verified && <CheckCircle className="h-5 w-5 text-m4-yellow" />}
              </Link>

              {/* Metadata */}
              <div className="flex flex-wrap gap-6 text-sm text-neutral-400 mb-8">
                {song.bpm && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {song.bpm} BPM
                  </div>
                )}
                {song.key && (
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4" />
                    Key of {song.key}
                  </div>
                )}
                {song.scripture_ref && (
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    {song.scripture_ref}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Play className="h-4 w-4" />
                  {song.play_count?.toLocaleString() || 0} plays
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={handlePlay}
                  className="bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold uppercase tracking-wider px-8"
                  data-testid="main-play-btn"
                >
                  {isCurrentSong && isPlaying ? (
                    <><Pause className="h-5 w-5 mr-2" fill="black" /> Playing</>
                  ) : (
                    <><Play className="h-5 w-5 mr-2" fill="black" /> Play</>
                  )}
                </Button>

                <Button
                  variant="outline"
                  className="border-neutral-700 text-white hover:bg-neutral-800"
                  onClick={() => toast.info('Added to favorites')}
                  data-testid="favorite-btn"
                >
                  <Heart className="h-5 w-5 mr-2" />
                  Favorite
                </Button>

                <Button
                  variant="outline"
                  className="border-neutral-700 text-white hover:bg-neutral-800"
                  onClick={handleShare}
                  data-testid="share-btn"
                >
                  <Share2 className="h-5 w-5 mr-2" />
                  Share
                </Button>

                {!song.is_free && (
                  <Button
                    variant="outline"
                    className="border-m4-yellow text-m4-yellow hover:bg-m4-yellow hover:text-black"
                    data-testid="download-btn"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Download ₹{song.price}
                  </Button>
                )}

                {song.youtube_url && (
                  <a href={song.youtube_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="border-neutral-700 text-white hover:bg-neutral-800">
                      <ExternalLink className="h-5 w-5 mr-2" />
                      YouTube
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Description & Lyrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Description */}
            {song.description && (
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
                <h3 className="font-heading text-xl font-bold text-white mb-4">About This Song</h3>
                <p className="text-neutral-300 leading-relaxed">{song.description}</p>
              </div>
            )}

            {/* Lyrics */}
            {song.lyrics && (
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
                <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-m4-yellow" />
                  Lyrics
                </h3>
                <div className="font-scripture text-neutral-300 leading-loose whitespace-pre-wrap" data-testid="lyrics-section">
                  {renderLyricsWithScripture(song.lyrics)}
                </div>
                {lyricsData?.scripture_references?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-neutral-800">
                    <p className="text-sm text-m4-yellow uppercase tracking-wider mb-3">Scripture References</p>
                    <div className="flex flex-wrap gap-2">
                      {lyricsData.scripture_references.map((ref, i) => (
                        <span key={i} className="bg-neutral-800 text-neutral-300 px-3 py-1 text-sm">
                          {ref.reference}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SongDetail;
